import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork, UtilityService } from 'app/core/core.module';
import { FileService } from 'app/core/file.service';
import { InvoiceService } from 'app/core/invoice.service';
import {
    Attachment,
    CustomerProductPriceOverride,
    Invoice,
    InvoiceLine,
    InvoicePaymentMethod,
    InvoiceStatus,
    InvoiceType,
    PaymentLine,
    Product,
    ProductCategory,
    ProductUnitOfMeasure,
} from 'app/core/model/entity-model';
import { ReportService } from 'app/core/report.service';
import { Utility } from 'app/core/utility';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { InputDialogComponent, SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { InvoiceLineDialogComponent } from '../invoice-line-dialog/invoice-line-dialog.component';

@Component({
    standalone: false,
    selector: 'app-invoice-detail',
    templateUrl: './invoice-detail.component.html',
    styleUrls: ['./invoice-detail.component.scss'],
})
export class InvoiceDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    invoiceId: any;

    invoice: Invoice;
    invoiceLines: InvoiceLine[];
    customerProductPriceOverrides: CustomerProductPriceOverride[] = [];
    paymentLines: PaymentLine[];
    customers: any[];
    customersDS: DataSource;
    productCategories: ProductCategory[];
    invoiceTypes: InvoiceType[];
    paymentMethods: InvoicePaymentMethod[];
    InvoiceStatuses = InvoiceStatus.Statuses;
    creditNumbers: { id: number; invoiceNumber: number }[];
    creditAmount: number = 0;

    uploadUrl: string;
    uploadHeader: any;
    attachments: Attachment[];

    deliveryOptions: any;
    readOnly = true;

    ready = false;
    shipCountryIsUS = true;
    shipCountry: any;
    countries = ['US', 'OTHER'];
    states: any;
    processing = false;
    showAsDialog = false;

    isToastVisible = false;
    validationGroup = 'mainValidationGroup';
    errorAction = 'saving';
    saving = false;
    title: any;

    taxableSubTotal: number;
    initialTotal: number;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: BrewersUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        protected location: Location,
        private fileService: FileService,
        private reportService: ReportService,
        private invoiceService: InvoiceService,
        private utility: UtilityService
    ) {}

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    async ngOnInit() {
        this.appInfo.pleaseWaitShow();
        this.paymentMethods = (await this.uow.invoicePaymentMethods.whereActive('displayOrder')) as InvoicePaymentMethod[];
        this.invoiceTypes = await this.uow.invoiceTypes.whereActive('displayOrder');
        this.states = this.uow.lookups.states;
        this.productCategories = await this.uow.productCategories.whereActive('displayOrder, name');
        const materials = await this.uow.productMaterials.whereActive();
        const stautes = await this.uow.invoiceStatuses.whereActive();
        const taxCodes = await this.uow.customerTaxCodes.whereActive();

        this.route.params.subscribe(async (params: Params) => {
            this.appInfo.pleaseWaitShow();
            await this.getCustomers();
            this.invoiceId = params['id'];
            await this.getData();

            this.uploadUrl = this.fileService.getUploadUrl(this.invoice.id, FileService.Mode.Invoice);
            this.uploadHeader = this.fileService.getUploadHeader();
            this.appInfo.pleaseWaitHide();
        });
    }

    async getData() {
        const today = new Date();
        if (this.invoiceId === 'new') {
            this.invoice = this.uow.invoices.createEntity({
                statusId: InvoiceStatus.Statuses.New,
                paymentMethodId: 1,
                created: today,
                invoiceDate: today,
                dueDate: moment(today).add(30, 'days').toDate(),
                createdBy: this.auth.currentUser.userName,
                invoiceCode: crypto.randomUUID(),
                invoiceTypeId: Invoice.Types.Invoice,
            }) as Invoice;
            this.invoiceLines = [];
        } else {
            this.invoice = (await this.uow.invoices.byId(
                this.invoiceId,
                'customer, invoiceLines.product, invoiceLines.load.loadAdditives, paymentLines, referencedInvoice'
            )) as Invoice;
            this.invoiceLines = this.invoice.invoiceLines.filter((l) => !l.deleted).slice();
            await this.getCreditData();
            await this.getAttachments();
        }

        this.customerProductPriceOverrides = this.invoice.customerId ? await this.uow.getCustomerProductPriceOverrides(this.invoice.customerId) : [];

        this.initialTotal = this.invoice.total;
        if (!this.showAsDialog) this.setTitle();
        this.setPermissions();
        this.calcTotals();
    }

    async getCustomers() {
        this.customers = (await this.uow.customers.getActiveCustomers(null, 'id, customerUnixKey, companyName')) as any[];
        this.customers.forEach((customer) => {
            customer.codeDisplayName = `${customer.customerUnixKey ?? ''} --- ${customer.companyName ?? ''}`;
        });

        this.customersDS = new DataSource({
            store: this.customers as any[],
            paginate: true,
        });
    }

    async getCreditData() {
        this.creditAmount = await this.invoiceService.getCreditAmount(this.invoice.id);
        this.creditNumbers = await this.invoiceService.getCreditNumbers(this.invoice.id);
    }

    hasChanges() {
        return this.uow.hasChanges();
    }
    setTitle() {
        if (this.invoiceId === 'new') this.title = 'Invoice Detail - New';
        else this.title = `${this.invoice.customer?.companyName}# ${this.invoice.invoiceNumber}`;

        this.appInfo.title = this.title;
    }
    setPermissions() {
        this.readOnly = this.invoice.statusId !== InvoiceStatus.Statuses.New;
    }

    rowPrepare(e) {}

    onCellPrepared(e) {
        if (e.rowType !== 'data' || e.column.dataField !== 'price') return;

        const line = e.data as InvoiceLine;
        const hasActiveOverride = this.customerProductPriceOverrides.some((override) => override.productId === line.productId && override.isActive);
        if (hasActiveOverride) e.cellElement.className = e.cellElement.className + ' price-override';
    }

    async onRowDoubleClick(e) {
        if (this.readOnly) return;
        this.openLine(e.data);
    }

    async receivePayment() {
        await this.save();
        const fullPaymentReceived = await this.invoiceService.receivePayment(this.invoice);
        if (!fullPaymentReceived) {
            this.router.navigate([`/payments/new/${this.invoice.customerId}/${this.invoice.id}`]);
        } else {
            this.appInfo.pleaseWaitShow();
            await this.getData();
            this.appInfo.pleaseWaitHide();
        }
    }

    async delete() {
        if (this.invoice.quickBooksId) {
            const result = await confirm(
                `This invoice has already been exported to QuickBooks.  If you continue deleting, you will need to manually delete it from QuickBooks.  Continue?`,
                this.appInfo.appTitle
            );
            if (!result) return;
        } else {
            const result = await confirm(`Are you sure you want to delete this invoice?`, this.appInfo.appTitle);
            if (!result) return;
        }

        this.appInfo.pleaseWaitShow();

        this.invoice.deleted = new Date();
        this.invoice.deletedBy = this.auth.currentUser.userName;
        this.invoice.invoiceLines.forEach((line) => {
            line.loadId = null;
            line.deleted = new Date();
            line.deletedBy = this.auth.currentUser.userName;
        });

        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
        this.router.navigate(['/invoices/overview']);
    }

    async customerSelectionChanged(e) {
        this.appInfo.pleaseWaitShow();
        const customer = await this.uow.customers.byId(this.invoice.customerId);
        this.invoice.customer = customer;
        this.invoice.customerTaxCodeId = customer?.taxCodeId;
        this.copyBilling();
        this.calcTotals();
        this.appInfo.pleaseWaitHide();
    }

    deliveryOptionSelectionChanged(e) {}

    freightChange = (e) => {
        this.calcTotals();
        // tslint:disable-next-line:semicolon
    };

    async copyBilling() {
        if (!this.invoice) return;

        const customer = this.invoice.customer;
        this.invoice.billToContactName = customer?.contactName;
        this.invoice.billToAddress1 = customer?.billAddress1;
        this.invoice.billToAddress2 = customer?.billAddress2;
        this.invoice.billToCity = customer?.billCity;
        this.invoice.billToState = customer?.billState;
        this.invoice.billToZip = customer?.billZip;
        this.invoice.billToContactName = customer?.contactName;
        this.invoice.billToCountry = customer?.billCountry;
        this.invoice.billToEmail = customer?.email;
        this.invoice.billToPhone = customer?.phone;
    }

    calcTotals() {
        // console.log('calcTotal');
        this.calcSubTotal();
        this.calcTax();

        if (this.invoice.total !== Utility.round(this.invoice.subTotal, 2) + Utility.round(this.invoice.tax, 2))
            this.invoice.total = Utility.round(this.invoice.subTotal + this.invoice.tax, 2);
    }

    calcSubTotal() {
        let subTotal = 0;
        let taxableSubTotal = 0;
        this.invoiceLines
            .filter((x) => !x.deleted)
            .forEach((line) => {
                subTotal += Utility.round(line.quantity * line.price, 2);
                if (line.isTaxable) taxableSubTotal += Utility.round(line.quantity * line.price, 2);
            });
        if (this.invoice.subTotal !== Utility.round(subTotal, 2)) this.invoice.subTotal = Utility.round(subTotal, 2);
        this.taxableSubTotal = Utility.round(taxableSubTotal, 2);
    }

    calcTax() {
        let tax = 0;
        tax = Utility.round(Utility.SalesTaxRate * this.taxableSubTotal, 2);
        if (this.invoice.tax !== Utility.round(tax, 2)) this.invoice.tax = Utility.round(tax, 2);
    }

    onTermsChanged = (e) => {
        let dayOffset = 1;
        if (e.selectedItem) dayOffset = e.selectedItem.dayOffset;
        const dueDate = moment(this.invoice.invoiceDate).add(dayOffset, 'days').toDate();
        this.invoice.dueDate = dueDate;
    };

    async addProductDelivery(invoiceLines: InvoiceLine[]) {
        if (!invoiceLines || invoiceLines.length === 0) return;

        let error = '';
        if (invoiceLines.some((invoiceLine) => invoiceLine.isAdditive)) error = 'Cannot create product deliveries for additives';
        else if (invoiceLines.some((invoiceLine) => invoiceLine.isMisc)) error = 'Cannot create product deliveries for misc lines';
        else if (!invoiceLines[0].productId || !invoiceLines.every((invoiceLine) => invoiceLine.productId === invoiceLines[0].productId))
            error = 'All lines must have the same product';
        if (error) {
            await alert(error, this.appInfo.settings.company);
            return;
        }

        const quantity = _(invoiceLines)
            .sumBy((invoiceLine) => invoiceLine.quantity)
            .valueOf();

        const product = invoiceLines[0].product;
        if (product.deliveryProductId) await this.addLineFromDeliveryProduct(product.deliveryProductId, quantity);
        else await this.openLine(null, quantity, 'delivery');
    }

    async addLineFromDeliveryProduct(deliveryProductId: number, quantity: number) {
        const deliveryProduct = await this.uow.products.byId(deliveryProductId, 'inventoryUom');
        const line = this.uow.invoiceLines.createEntity({
            productId: deliveryProductId,
            partNumber: deliveryProduct.partNumber,
            unit: deliveryProduct.inventoryUom?.name,
            description: deliveryProduct.description,
            price: deliveryProduct.basePrice,
            isTaxable: deliveryProduct.category.isTaxable,
            quantity: quantity,
            invoiceId: this.invoice.id,
            lineNumber: this.nextLineNumber(),
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        });
        this.invoiceLines.push(line);
        await this.save();
        await this.linesGrid.instance.refresh();
    }

    async openLine(invoiceLine?: InvoiceLine, quantity?: number, mode?: 'delivery') {
        if (invoiceLine && invoiceLine.product?.material.name === 'DELIVERY') mode = 'delivery';
        await this.save();
        const modalRef = this.modalService.show(InvoiceLineDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                invoiceId: this.invoice.id,
                lineNumber: this.nextLineNumber(),
                invoiceLine: invoiceLine,
                mode: mode,
                quantity: quantity,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                if (!this.invoiceLines.includes(result.value)) this.invoiceLines.push(result.value);
                await this.save();
                await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    async addDelivery() {
        await this.save();
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Delivery',
                dialogPrompt: 'Price',
                dialogValue: 100,
                mode: 'text',
                checkNumeric: true,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && result.value) {
                const line = this.uow.invoiceLines.createEntity({
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    description: 'Delivery',
                    invoiceId: this.invoice.id,
                    isMisc: true,
                    lineNumber: this.nextLineNumber(),
                    partNumber: 'Delivery',
                    price: result.value,
                    productId: Product.Types.Delivery,
                    quantity: 1,
                    unit: 'EA',
                    unitOfMeasureId: ProductUnitOfMeasure.Types.Each,
                });
                this.invoiceLines.push(line);
                await this.save();
                await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    async addService() {
        await this.save();

        const services = await this.uow.invoiceServices.whereActive('displayOrder, name');

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Service',
                mode: 'select',
                dialogValueArray: services.map((s) => s.name),
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                const line = this.uow.invoiceLines.createEntity({
                    quantity: 1,
                    price: 0,
                    invoiceId: this.invoiceId,
                    lineNumber: this.nextLineNumber(),
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    partNumber: result.value,
                    isMisc: true,
                });
                this.invoiceLines.push(line);
                await this.save();
                await this.linesGrid.instance.refresh();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async removeLine(invoiceLines?: InvoiceLine[]) {
        const result = await confirm(`Are you sure you want to remove the selected invoice line?`, this.appInfo.appTitle);
        if (!result) return;

        const save = await this.save();
        if (!save) return;

        for (let index = 0; index < invoiceLines.length; index++) {
            const line = invoiceLines[index];
            line.loadId = null;
            this.invoiceLines.splice(this.invoiceLines.indexOf(line), 1);
            line.deleted = new Date();
            line.deletedBy = this.auth.currentUser.userName;
        }

        this.updateDisplayOrder();
        await this.save();
        await this.getData();
        this.calcTotals();
        await this.save();
    }

    addMiscLine() {}

    toggleIsTaxable(lines: InvoiceLine[]) {
        if (this.readOnly || !lines || lines.length === 0) return;

        const isTaxable = lines.every((line) => line.isTaxable);
        lines.forEach((line) => (line.isTaxable = !isTaxable));
        this.calcTotals();
    }

    async save() {
        const success = await Promise.resolve(this.validate());
        if (!success) return false;
        this.appInfo.pleaseWaitShow();
        this.isToastVisible = true;
        console.log('saveCalc');
        this.calcTotals();

        if (!this.invoice.invoiceNumber) this.invoice.invoiceNumber = (await this.utility.getNextNumber('InvoiceNumber')) as any;

        await this.uow.commit();
        if (this.invoiceId === 'new') {
            this.invoiceId = this.invoice.id;
            this.router.navigate(['/invoices/' + this.invoice.id], { replaceUrl: true });
            this.setTitle();
            this.uploadUrl = this.fileService.getUploadUrl(this.invoice.id, FileService.Mode.Invoice);
        }

        await this.invoiceService.updateBalances([this.invoice.id]);
        await this.getData();

        this.appInfo.pleaseWaitHide();
        return true;
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }
    closeDialog() {
        if (this.showAsDialog) {
            this.uow.rollback();
            this.result.next({});
        }
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    async email() {
        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'Invoice',
                entity: this.invoice,
                entityAttachments: this.attachments,
                to: this.invoice.customer?.email,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            modalRef.hide();
        });
    }

    async print() {
        await this.reportService.printInvoiceReport(this.invoice);
    }

    async getAttachments() {
        this.attachments = await this.uow.getAttachments(FileService.Mode.Invoice, this.invoice.id);
    }

    refreshAttachments() {
        this.attachments = _(this.attachments)
            .filter((a) => !a.deleted)
            .orderBy('name')
            .valueOf();
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        var response = this.fileService.parseUploadResponse(e);
        if (!response.success) alert(`Unable to upload the file(s). ${response.message}`, 'Upload Error');
        else await this.getAttachments();
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    openAttachment(attachment: Attachment) {
        if (!attachment) return;
        window.open(attachment.filename, '_blank');
    }

    async removeAttachment(attachment: Attachment) {
        if (!attachment) return;

        const success = await confirm('Are you sure you want to delete this attachment?', this.appInfo.settings.company);
        if (!success) return;

        await this.appInfo.pleaseWaitShow();
        attachment.deleted = new Date();
        attachment.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit([attachment]);
        await this.getAttachments();
        await this.appInfo.pleaseWaitHide();
    }

    async move(invoiceLine: InvoiceLine, direction: 'up' | 'down') {
        if (this.processing) return;
        this.processing = true;
        const increment = direction === 'down' ? 1 : -1;
        const otherInvoiceLine = this.invoiceLines.find((otherInvoiceLine) => otherInvoiceLine.lineNumber === invoiceLine.lineNumber + increment);
        if (otherInvoiceLine) {
            invoiceLine.lineNumber += increment;
            otherInvoiceLine.lineNumber -= increment;
            this.sort(this.invoiceLines);
            await this.save();
        }
        this.processing = false;
    }

    sort(invoiceLines: InvoiceLine[]) {
        invoiceLines.sort((a, b) => (a.lineNumber || 9999) - (b.lineNumber || 9999));
    }

    updateDisplayOrder() {
        _.orderBy(this.invoiceLines, 'lineNumber').forEach((invoiceLine, index) => {
            if (invoiceLine.lineNumber !== index + 1) invoiceLine.lineNumber = index + 1;
        });
    }

    nextLineNumber() {
        return (_(this.invoiceLines).maxBy((l) => l.lineNumber)?.lineNumber ?? 0) + 1;
    }
}
